import React from 'react';
import {Dialog} from '@bosch/react-frok';
import {t} from 'i18next';
import {CheckboxStyled, DeleteInfoStyled} from './DeleteItemDialog.style';
import {Accordion} from '../../Accordion/Accordion';
import {dynamicConfig} from '../../../init/config';
import {CountryPicker} from '../../LocalePicker/CountryPicker';
import {LanguagePicker} from '../../LocalePicker/LanguagePicker';
import {LocalePicker} from '../../LocalePicker/LocalePicker';
import {convertLocaleToRepresentationValue} from '../../../utils';

export interface DeleteDialogProps {
    readonly fileTitle: string;
    readonly countryDataSource: string[];
    readonly languageDataSource: string[];
    readonly localeDataSource: string[];
    readonly defaultLocale: string;
    readonly isCompleteToDelete: boolean;
    readonly countriesToDelete: string[];
    readonly languagesToDelete: string[];
    readonly localesToDelete: string[];
    setIsCompleteToDelete: (isComplete: boolean) => void;
    setCountriesToDelete: (countriesToDelete: string[]) => void;
    setLanguagesToDelete: (languagesToDelete: string[]) => void;
    setLocalesToDelete: (localesToDelete: string[]) => void;
    onCancel: () => void;
    onConfirm: () => void;
}

const countryRestriction: string[] = dynamicConfig.DLS_COUNTRIES;
const languageRestriction: string[] = dynamicConfig.DLS_LANGUAGES;

const filterLocales = (locales: string[]) => {
    let filteredLocales: string[] = [];
    let localeItem: string[] = [];
    if ((locales) && (locales.length > 0)) {
        filteredLocales = locales
            .map(locale => locale)
            .filter(locale => {
                localeItem = locale.split('_');
                if (localeItem.length >= 2) {
                    return (((countryRestriction.length === 0)
                            || ((countryRestriction.length > 0) && (countryRestriction.includes(localeItem[1].toUpperCase()))))
                        && ((languageRestriction.length === 0)
                            || ((languageRestriction.length > 0) && (languageRestriction.includes(localeItem[0])))))
                }

                return false;
            });
    }

    return filteredLocales;
};

/**
 * @name DeleteItemDialog
 * @description
 * A dialog to delete the selected file/folder
 */
export const DeleteItemDialog: React.VFC<DeleteDialogProps> = (props) => {
    const defaultLocale = convertLocaleToRepresentationValue(props.defaultLocale);

    const getCountryOptions = () => {
        return props.countryDataSource
            .filter(country => ((countryRestriction.length === 0)
                || ((countryRestriction.length > 0) && (countryRestriction.includes(country)))));
    };

    const getLanguageOptions = () => {
        return props.languageDataSource
            .filter(language => ((languageRestriction.length === 0)
                || ((languageRestriction.length > 0) && (languageRestriction.includes(language)))));
    };

    const getLocaleDataSource = () => {
        let locales: string[] = [];
        if ((props.localeDataSource) && (props.localeDataSource.length > 0)) {
            locales = filterLocales(props.localeDataSource);
        }

        return locales;
    };

    const getLocaleOptions = () => {
        let localeOptions: string[] = [];
        let localeItem: string[] = [];
        getLocaleDataSource().map(locale => {
            if (locale) {
                localeItem = locale.toLowerCase().split('_');
                if ((localeItem) && (localeItem.length >= 2)) {
                    localeOptions.push(convertLocaleToRepresentationValue(locale));
                }
            }
        });

        return localeOptions;
    };

    const onChangeIsCompleteHandler = () => {
        props.setIsCompleteToDelete(!props.isCompleteToDelete);
    };

    return (
        <Dialog
            modal
            open
            title={t('DELETE_DIALOG_TITLE', {fileTitle: props.fileTitle})}
            headline={t('DELETE_DIALOG_HEADLINE', {fileTitle: props.fileTitle})}
            cancelLabel={t('GENERAL_CANCEL_LABEL')}
            confirmLabel={t('DELETE_DIALOG_DELETE_LABEL')}
            onClose={props.onCancel}
            onCancel={props.onCancel}
            onConfirm={props.onConfirm}
        >
            <DeleteInfoStyled>
                {t('DELETE_DIALOG_DELETION_PERMANENT_INFO_TEXT', {locale: defaultLocale})}
            </DeleteInfoStyled>
            <CheckboxStyled key={'DeleteDialogIsComplete'}
                            id={'DeleteDialogIsComplete'}
                            label={t('DELETE_DIALOG_IS_COMPLETE')}
                            checked={props.isCompleteToDelete}
                            onChange={onChangeIsCompleteHandler}
            />
            <Accordion accordionHeadline={t('LOCALE_CAPTION')}>
                <DeleteInfoStyled>
                    {t('DELETE_DIALOG_DELETION_DEFAULT_LOCALE_PERMANENT_INFO_TEXT', {locale: defaultLocale})}
                </DeleteInfoStyled>
                <CountryPicker dataSource={getCountryOptions()}
                               defaultValue={''}
                               resetState={!!((props.isCompleteToDelete) && (props.countriesToDelete.length > 0))}
                               resetInput={true}
                               disabled={props.isCompleteToDelete}
                               onChange={(countrySelection: string[]) => props.setCountriesToDelete(countrySelection)}
                />
                <LanguagePicker dataSource={getLanguageOptions()}
                                defaultValue={''}
                                resetState={!!((props.isCompleteToDelete) && (props.languagesToDelete.length > 0))}
                                resetInput={true}
                                disabled={props.isCompleteToDelete}
                                onChange={(languageSelection: string[]) => props.setLanguagesToDelete(languageSelection)}
                />
                <LocalePicker dataSource={getLocaleOptions()}
                              defaultValue={''}
                              resetState={!!((props.isCompleteToDelete) && (props.localesToDelete.length > 0))}
                              resetInput={true}
                              disabled={props.isCompleteToDelete}
                              onChange={(localeSelection: string[]) => props.setLocalesToDelete(localeSelection)}
                />
            </Accordion>
        </Dialog>
    );
};
